/**
 * NetworkPrinterPlugin — pont TypeScript vers le plugin Capacitor natif Android.
 *
 * Ce plugin permet d'envoyer un buffer ESC/POS brut directement à une imprimante
 * réseau via socket TCP, contournant l'Edge Function print-document qui ne peut
 * pas atteindre le LAN local du commerçant.
 *
 * ⚠️ PAS d'import statique de '@capacitor/core' ici : le package est EXTERNAL
 * dans vite.config.ts (« Available in APK shell, not in web build ») — un import
 * statique laisse un spécificateur nu dans le bundle et blanchit l'écran au boot
 * (incident prod 2026-07-09, hotfix). L'accès passe par window.Capacitor, comme
 * partout ailleurs dans le codebase (cf. global.d.ts).
 *
 * Disponible uniquement sur la plateforme native Android dont l'APK embarque
 * NetworkPrinterPlugin.java (enregistré dans MainActivity) — les APK antérieurs
 * au rebuild n'ont pas le plugin : getNetworkPrinter() renvoie null et l'adapter
 * dégrade vers l'Edge Function.
 *
 * Invariant 4.B.4 — 0 any : tous les types sont stricts.
 * Invariant 4.B.2 — aucun accès Supabase ici (couche plugin pure).
 */

// ---------------------------------------------------------------------------
// Interface du plugin
// ---------------------------------------------------------------------------

/**
 * Options passées à NetworkPrinter.printRaw().
 * Correspondent exactement aux paramètres attendus par NetworkPrinterPlugin.java.
 */
export interface NetworkPrintRawOptions {
  /** Adresse IP de l'imprimante réseau (ex : "192.168.1.100"). */
  readonly host: string;
  /**
   * Port TCP de l'imprimante.
   * Défaut : 9100 (ESC/POS raw / JetDirect — standard universel).
   * Autres ports possibles : 9101 (certains Epson), 515 (LPD/LPR).
   */
  readonly port: number;
  /**
   * Buffer ESC/POS encodé en base64 (produit par buildEscPosReceipt / buildEscPosKitchen).
   * Encodage base64 obligatoire pour transiter de JS vers Android sans corruption binaire.
   */
  readonly dataBase64: string;
  /**
   * Timeout de connexion TCP en ms.
   * Défaut côté Java : 10 000 ms.
   * Réduire à 3 000–5 000 ms si on veut un feedback d'erreur rapide
   * (ex : imprimante éteinte sur réseau Wi-Fi lent).
   */
  readonly timeoutMs?: number;
}

/** Résultat d'une impression réseau réussie. */
export interface NetworkPrintRawResult {
  /** Toujours true si la méthode résout (les erreurs passent par reject). */
  readonly success: boolean;
}

/** Contrat TypeScript du plugin natif NetworkPrinter. */
export interface NetworkPrinterPlugin {
  /**
   * Ouvre une socket TCP vers host:port, envoie les octets ESC/POS,
   * flush et ferme. Résout { success: true } ou rejette avec un message d'erreur.
   */
  printRaw(options: NetworkPrintRawOptions): Promise<NetworkPrintRawResult>;
}

// ---------------------------------------------------------------------------
// Accès au plugin natif via le bridge window (pattern maison)
// ---------------------------------------------------------------------------

/** true si l'app tourne dans l'APK Capacitor (pattern window, cf. global.d.ts). */
export function isNativePlatform(): boolean {
  return window.Capacitor?.isNativePlatform?.() === true;
}

/**
 * Récupère le plugin natif NetworkPrinter depuis le bridge Capacitor.
 *
 * Renvoie null si :
 *   - plateforme web (pas d'APK) ;
 *   - APK antérieur au rebuild qui embarque NetworkPrinterPlugin.java
 *     (isPluginAvailable('NetworkPrinter') === false).
 *
 * L'appelant (network-printer-adapter) dégrade alors vers l'Edge Function.
 */
export function getNetworkPrinter(): NetworkPrinterPlugin | null {
  const cap = window.Capacitor;
  if (!cap || cap.isNativePlatform?.() !== true) return null;
  if (cap.isPluginAvailable?.('NetworkPrinter') !== true) return null;
  const plugin = cap.Plugins?.['NetworkPrinter'];
  return plugin ? (plugin as NetworkPrinterPlugin) : null;
}
